import React, { MutableRefObject, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput, registerObject } from "go-form";
import { ReactComponent as EditSVG } from "go-core/images/svg/actions/edit.svg";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	ContactApi,
	ContactApiSearchSelect,
	OrderApi,
	OrderItemApi,
	StockApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import CreateContactModalForm from "../../../../components/CreateContactModalForm";

interface Props {
	form: UseFormReturn<OrderApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<OrderApi>[]>>;
	order?: OrderApi;
	getExternalStates: (items: OrderItemApi[]) => Promise<void>;
	selectedContact: MutableRefObject<ContactApiSearchSelect | undefined>;
}

const OrderBasicInfoForm = ({ form, setErrors, order, getExternalStates, selectedContact }: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		register,
		control,
		setValue,
		watch,
	} = form;
	const [showContactModal, setShowContactModal] = useState<boolean>(false);
	const [newContact, setNewContact] = useState<ContactApi>();
	const [contact, setContact] = useState<ContactApi>(order?.contact as ContactApi);
	const organization = useSelector(selectOrganization);
	const [changeFromSelectToTextInput, setChangeFromSelectToTextInput] = useState<boolean>(false);

	const searchStocks = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getStockSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onChangeStock = (stock: StockApi) => {
		registerObject(register, `stock`, ["name", "id"]);
		setValue("stock", { name: stock.name, id: stock.id } as StockApi);
	};

	const searchContacts = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = { ...params, include: "document_transfer" };

		return api.organization().getContactSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const onCreateContact = (value?: string) => {
		setNewContact({ name: value } as ContactApi);
		setShowContactModal(true);
	};

	const onSaveContact = (contact: ContactApi) => {
		setValue("contact_id", contact.id);
		setContact(contact);
		setShowContactModal(false);
	};

	const contactSelectCustomActions = [
		<EditSVG
			key="select"
			className="preview-option edit-icon"
			onMouseDown={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
			onClick={() => setChangeFromSelectToTextInput(true)}
		/>,
	];

	const contactTextInputCustomActions = [
		<EditSVG key="input" className="icon edit-icon" onClick={() => setChangeFromSelectToTextInput(false)} />,
	];

	const contactChangeHandler = (contactId: string, contactObject: ContactApiSearchSelect) => {
		selectedContact.current = contactObject;
		getExternalStates(watch("items"));
	};

	const orderedAtValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const stockIdValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const isStockInputDisabled = () => {
		if (!watch("id")) return false;
		if (!watch("status")) return false;
		return watch("status") !== "UNLINKED" && watch("status") !== "DRAFT" && watch("status") !== "VOID";
	};

	return (
		<fieldset className="form-group">
			<div className="row">
				<div className="col-lg-10">
					<div className="row">
						<div className="col-md-6">
							<FormInput type="hidden" name="id" errors={errors} register={register} />
							<FormInput
								label={t("common.word.reference_number")}
								register={register}
								name="reference_number"
								errors={errors}
							/>
						</div>
						<div className="col-md-6">
							<FormDatePicker
								errors={errors}
								control={control}
								name="ordered_at"
								allowClear={false}
								label={t("modules.order.field.ordered_at.title")}
								customValidationConfig={orderedAtValidationConfig}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<FormSelectGroup
								control={control}
								loadOptions={searchStocks}
								label={t("modules.order.field.from_stock.title")}
								onChange={(id, stock) => onChangeStock(stock)}
								name="stock_id"
								isClearable={false}
								getOptionValue={(opt) => opt.id}
								defaultValue={{
									label: watch("stock")?.name,
									id: watch("stock_id"),
								}}
								errors={errors}
								customValidationConfig={stockIdValidationConfig}
								data-testid="stock_id"
								disabled={isStockInputDisabled()}
							/>
						</div>
						<div className="col-md-6">
							{changeFromSelectToTextInput ? (
								<FormInput
									name="external_contact_name"
									errors={errors}
									register={register}
									label={t("common.word.contact")}
									customActions={contactTextInputCustomActions}
									defaultValue={order?.external_contact_name}
								/>
							) : (
								<>
									<FormSelectGroup
										control={control}
										loadOptions={searchContacts}
										label={t("common.word.contact")}
										name="contact_id"
										getOptionValue={(opt) => opt.id}
										defaultValue={{
											label: contact?.name,
											id: contact?.id,
										}}
										onCreateOption={onCreateContact}
										path={`/${organization.id}/contacts/`}
										errors={errors}
										customActions={!order?.contact_id ? contactSelectCustomActions : []}
										onChange={contactChangeHandler}
										data-testid="contact_id"
									/>
									{showContactModal && (
										<CreateContactModalForm
											isShown={showContactModal}
											value={newContact?.name || ""}
											onHide={() => setShowContactModal(false)}
											onSave={(data) => onSaveContact(data)}
										/>
									)}
								</>
							)}
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<FormInput
								label={t("common.word.comment.title")}
								register={register}
								name="comment"
								errors={errors}
							/>
						</div>
					</div>
				</div>
			</div>
		</fieldset>
	);
};

export default OrderBasicInfoForm;
